import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, fmt } from "@/lib/engagements";

const monthValue = (d = new Date()) => d.toISOString().slice(0, 7);

export const HoursSummary = ({ canSeeAll, reloadKey }) => {
  const firstOfMonth = `${monthValue()}-01`;
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [staff, setStaff] = useState("");
  const [data, setData] = useState({ rows: [], total_hours: 0, internal_rows: [], internal_hours: 0 });

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/timesheets/summary`, {
      params: { from_date: fromDate, to_date: toDate, staff },
    });
    setData(data);
  }, [fromDate, toDate, staff]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const staffNames = useMemo(
    () => Array.from(new Set(data.rows.map((r) => r.staff_name))).sort(),
    [data.rows]
  );

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-extrabold">Hours per client</h3>
          <p className="text-sm text-zinc-500">
            {canSeeAll ? "Every staff member's" : "Your"} logged hours grouped by client.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">From</Label>
            <Input data-testid="summary-from" type="date" value={fromDate}
                   onChange={(e) => setFromDate(e.target.value)} className="mt-1 h-9 rounded-sm tabular" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">To</Label>
            <Input data-testid="summary-to" type="date" value={toDate}
                   onChange={(e) => setToDate(e.target.value)} className="mt-1 h-9 rounded-sm tabular" />
          </div>
          {canSeeAll && (
            <select data-testid="summary-staff-filter" value={staff}
                    onChange={(e) => setStaff(e.target.value)}
                    className="h-9 rounded-sm border border-input bg-white px-3 text-sm">
              <option value="">All staff</option>
              {staffNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-border bg-white">
        <table data-testid="summary-table" className="w-full text-left text-xs">
          <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-white">
            <tr>
              <th className="px-3 py-2">Staff</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2 text-right">Entries</th>
              <th className="px-3 py-2 text-right">Total hours</th>
            </tr>
          </thead>          <tbody>
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={4} data-testid="summary-empty" className="px-3 py-8 text-center text-sm text-zinc-500">
                  No hours logged in this date range.
                </td>
              </tr>
            )}
            {data.rows.map((r) => (
              <tr key={`${r.staff_name}-${r.client}`}
                  data-testid={`summary-row-${r.staff_name}-${r.client}`}
                  className="border-t border-border hover:bg-zinc-50">
                <td className="px-3 py-2 font-semibold">{r.staff_name}</td>
                <td className="px-3 py-2">{r.client}</td>
                <td className="px-3 py-2 text-right tabular text-zinc-600">{r.entries}</td>
                <td className="px-3 py-2 text-right tabular font-semibold">{r.hours.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-300 bg-zinc-50">
                <td colSpan={3} className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">
                  Total
                </td>
                <td data-testid="summary-total" className="px-3 py-2 text-right font-display text-base font-extrabold tabular">
                  {data.total_hours.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {data.internal_rows?.length > 0 && (
        <div data-testid="internal-summary" className="border border-border bg-white">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              Internal (not charged to clients)
            </span>
            <span data-testid="internal-total" className="text-xs font-semibold tabular">
              {data.internal_hours.toFixed(2)} hrs
            </span>
          </div>
          <div className="divide-y divide-border">
            {data.internal_rows.map((r) => (
              <div key={`${r.staff_name}-${r.entry_type}`}
                   data-testid={`internal-row-${r.staff_name}-${r.entry_type}`}
                   className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="font-semibold">{r.staff_name}</span>
                <span className="text-zinc-600">{r.label}</span>
                <span className="tabular">{r.entries} entries</span>
                <span className="font-semibold tabular">{r.hours.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export const DayTracker = ({ canSeeAll, reloadKey }) => {
  const [month, setMonth] = useState(monthValue());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/timesheets/compliance`, { params: { month } });
    setData(data);
  }, [month]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const rows = data?.staff || [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-extrabold">Working day tracker</h3>
          <p className="text-sm text-zinc-500">
            8 hours required every Monday to Friday. Days below 8 hours are flagged
            {data ? ` (${fmt(data.from_date)} – ${fmt(data.to_date)})` : ""}. Click a row to see the
            exact dates not keyed in yet.
          </p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Month</Label>
          <Input data-testid="tracker-month" type="month" value={month}
                 onChange={(e) => setMonth(e.target.value)} className="mt-1 h-9 rounded-sm tabular" />
        </div>
      </div>

      <div className="border border-border bg-white">
        <div className="grid grid-cols-[minmax(140px,1fr)_110px_110px_110px_120px] bg-zinc-900 px-4 py-2 text-[10px] uppercase tracking-widest text-white">
          <span>Staff</span>
          <span className="text-right">Working days</span>
          <span className="text-right">Logged hrs</span>
          <span className="text-right">Required hrs</span>
          <span className="text-right">Incomplete</span>
        </div>
        {rows.length === 0 && (
          <div data-testid="tracker-empty" className="p-8 text-center text-sm text-zinc-500">
            No staff to show.
          </div>
        )}
        {rows.map((r) => {
          const isOpen = open === r.user_id;
          return (
            <div key={r.user_id} className="border-t border-border">
              <button
                data-testid={`tracker-row-${r.staff_id}`}
                onClick={() => setOpen(isOpen ? null : r.user_id)}
                className="grid w-full cursor-pointer grid-cols-[minmax(140px,1fr)_110px_110px_110px_120px] items-center px-4 py-3 text-left text-sm transition-colors hover:bg-zinc-50"
              >
                <span className="flex items-center gap-2 font-semibold">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                  {r.staff_name}
                  <span className="ml-1 text-[10px] uppercase tracking-widest text-zinc-400">{r.role}</span>
                </span>
                <span className="text-right tabular text-zinc-600">{r.working_days}</span>
                <span className="text-right tabular">{r.logged_hours.toFixed(2)}</span>
                <span className="text-right tabular text-zinc-500">{r.required_hours.toFixed(2)}</span>
                <span data-testid={`tracker-incomplete-${r.staff_id}`} className="text-right">
                  {r.incomplete_days > 0 ? (
                    <span
                      title="Click to see which dates are not keyed in yet"
                      className="inline-flex items-center gap-1 rounded-sm border border-[#FECACA] bg-[#FEF2F2] px-2 py-0.5 font-display text-base font-extrabold text-red-700 underline decoration-dotted underline-offset-2"
                    >
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {r.incomplete_days}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> All done
                    </span>
                  )}
                </span>
              </button>

              {isOpen && (
                <div className="border-t border-border bg-zinc-50 px-4 py-3">
                  {r.days.length === 0 ? (
                    <p className="text-xs text-zinc-500">Every working day this month has 8 hours logged.</p>
                  ) : (
                    <table className="w-full text-left text-xs">
                      <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                        <tr>
                          <th className="py-1 pr-4">Date</th>
                          <th className="py-1 pr-4">Day</th>
                          <th className="py-1 pr-4 text-right">Logged</th>
                          <th className="py-1 pr-4 text-right">Short by</th>
                          <th className="py-1">Flag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.days.map((d) => (
                          <tr key={d.date} data-testid={`tracker-day-${r.staff_id}-${d.date}`}
                              className="border-t border-zinc-200">
                            <td className="py-1.5 pr-4 tabular">{fmt(d.date)}</td>
                            <td className="py-1.5 pr-4">
                              {new Date(d.date).toLocaleDateString("en-GB", { weekday: "long" })}
                            </td>
                            <td className="py-1.5 pr-4 text-right tabular">{d.hours.toFixed(2)}</td>
                            <td className="py-1.5 pr-4 text-right tabular font-semibold text-red-700">
                              {d.missing.toFixed(2)}
                            </td>
                            <td className="py-1.5">
                              <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-medium ${
                                d.status === "Missing"
                                  ? "border-[#FECACA] bg-[#FEF2F2] text-[#991B1B]"
                                  : "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]"
                              }`}>
                                {d.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
